import { useState, useEffect } from 'react';
import { api } from '../api/client';
import { BackupHealth } from '../api/types';
import { RunList } from '../components/RunList';

export function Dashboard() {
  const [health, setHealth] = useState<BackupHealth | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchHealth() {
      try {
        const data = await api.get<BackupHealth>('/orgs');
        if (!cancelled) {
          setHealth(data);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load backup health');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    fetchHealth();
    return () => { cancelled = true; };
  }, []);

  if (isLoading) {
    return (
      <div role="status" aria-live="polite">
        <h2>Dashboard</h2>
        <p>Loading backup health data…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div role="alert">
        <h2>Dashboard</h2>
        <div className="card" style={{ borderLeft: '4px solid var(--color-error)' }}>
          <p style={{ color: 'var(--color-error)' }}>{error}</p>
        </div>
      </div>
    );
  }

  if (!health) return null;

  return (
    <div>
      <h2>Backup Health</h2>

      {/* Stats */}
      <div className="stat-grid" role="region" aria-label="Backup statistics">
        <div className="stat-card">
          <div className="label">Last Successful Run</div>
          <div className="value" style={{ fontSize: '1rem' }}>
            {health.lastSuccessfulRun
              ? new Date(health.lastSuccessfulRun).toLocaleString()
              : 'Never'}
          </div>
        </div>
        <div className="stat-card">
          <div className="label">Objects Backed Up</div>
          <div className="value">{health.totalObjectsBacked.toLocaleString()}</div>
        </div>
        <div className="stat-card">
          <div className="label">Total Records</div>
          <div className="value">{health.totalRecordsBacked.toLocaleString()}</div>
        </div>
        <div className="stat-card">
          <div className="label">Failed Objects</div>
          <div
            className="value"
            style={{ color: health.failedObjectsCount > 0 ? 'var(--color-error)' : 'inherit' }}
          >
            {health.failedObjectsCount}
          </div>
        </div>
      </div>

      {/* Recent Runs */}
      <h3 style={{ marginTop: '2rem' }}>Recent Backup Runs</h3>
      <RunList runs={health.recentRuns} />
    </div>
  );
}
